import React, { useEffect, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField } from '@material-ui/core'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { LoadableButton } from '../../../components/Form'
import { ICorporation } from '../../../graphql/types/businesses'
import { useAllCountries } from '../../../graphql/hooks/localization'
import { Loader } from '../../../components/UI'
import { ICity } from '../../../graphql/types/localization'
import { CorporateTypesMultiSelect } from '../corporateTypes/CorporateTypesMultiSelect'

interface IProps {
  loading: boolean
  corporation?: ICorporation | null
  onSubmit: (values: ICorporateFormValues) => void
  onCancel: () => void
}

export interface ICorporateFormValues {
  corporationName: string
  countryId: number | null
  citiesIds: number[]
  corporationTypesIds: number[]
}

const corporateSchema = Yup.object().shape({
  corporationName: Yup.string().required('Обязательное поле'),
  countryId: Yup.number()
    .integer()
    .nullable(),
  citiesIds: Yup.array().of(Yup.number()),
  corporationTypesIds: Yup.array().of(Yup.number()),
})

export const CorporateForm: React.FC<IProps> = ({ loading, corporation, onSubmit, onCancel }) => {
  const { countries, loading: loadingCountries } = useAllCountries()
  const [cities, setCities] = useState<ICity[]>([])

  const formik = useFormik<ICorporateFormValues>({
    initialValues: {
      corporationName: corporation?.corporationName ?? '',
      countryId: corporation?.country?.id ?? null,
      citiesIds: corporation?.cities?.map(c => c.id) ?? [],
      corporationTypesIds: corporation?.corporationTypes?.map(t => t.id) ?? [],
    },
    validationSchema: corporateSchema,
    onSubmit(values, formikHelpers) {
      onSubmit(values)
    },
  })

  const handleChangeCountryId = (id: string, resetCities: boolean = true) => {
    if (resetCities) {
      formik.setFieldValue('citiesIds', [])
    }

    if (id === '') {
      formik.setFieldValue('countryId', null)
      setCities([])
      return
    }

    const selectedCountry = countries.find(c => c.id === parseInt(id))
    if (selectedCountry) {
      formik.setFieldValue('countryId', selectedCountry.id)
      setCities([...selectedCountry.cities])
    }
  }

  const handleSelectCity = (city: ICity) => {
    if (formik.values.countryId !== city.countryId) {
      return
    }

    if (values.citiesIds.includes(city.id)) {
      formik.setFieldValue(
        'citiesIds',
        formik.values.citiesIds.filter(c => c !== city.id)
      )
    } else {
      formik.setFieldValue('citiesIds', [...formik.values.citiesIds, city.id])
    }
  }

  useEffect(
    () => {
      if (!loadingCountries && corporation?.country) {
        handleChangeCountryId(`${corporation.country.id}`, false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [corporation?.id, loadingCountries]
  )

  const { values, errors, handleChange, setFieldValue, submitForm } = formik

  return (
    <div>
      <Box mb={2}>
        <TextField
          label='Название корпорации'
          name='corporationName'
          value={values.corporationName}
          onChange={handleChange}
          required
          fullWidth
          margin='normal'
          error={!!errors.corporationName}
          helperText={errors.corporationName}
        />
      </Box>

      <Box mb={2}>
        <CorporateTypesMultiSelect
          values={values.corporationTypesIds}
          onChange={types => setFieldValue('corporationTypesIds', types)}
        />
      </Box>

      <Box>
        {loadingCountries ? (
          <Loader />
        ) : (
          <TextField
            placeholder='Страна'
            label='Страна'
            value={values.countryId ?? ''}
            onChange={e => handleChangeCountryId(e.target.value)}
            error={!!errors.countryId}
            margin='normal'
            fullWidth
            select
          >
            <MenuItem value={''}>{'Без страны'}</MenuItem>
            {countries.map(country => (
              <MenuItem key={`country-${country.id}`} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      {!loadingCountries && cities.length > 0 && (
        <Box mb={4}>
          {cities.map(city => (
            <FormControlLabel
              key={`city-${city.id}`}
              control={
                <Checkbox
                  checked={values.citiesIds.includes(city.id) ?? false}
                  onChange={() => handleSelectCity(city)}
                  value={city.id}
                />
              }
              label={city.name}
            />
          ))}
        </Box>
      )}

      <Box pt={4} pb={1} display='flex' flexDirection='row' justifyContent='space-between'>
        <Button onClick={onCancel}>Отмена</Button>
        <LoadableButton loading={loading} color='primary' variant='contained' onClick={submitForm}>
          Сохранить
        </LoadableButton>
      </Box>
    </div>
  )
}
